import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "../app/AppShell";
import { Button } from "../components/Button";
import { Alert, EmptyState, Loading } from "../components/Card";
import { Input, Select } from "../components/Field";
import { GraphCanvas, NODE_H, graphView, layoutGraph } from "../components/Graph";
import { IssueDetailModal } from "../components/IssueDetail";
import { PageHead } from "../components/Page";
import { projectApi } from "../lib/project";
import type { Project } from "../lib/project";
import { errText, tasksApi } from "../lib/tasks";
import type { Issue } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 일감 › 그래프(#/graph) — t-in-port-work-graph ══
   `GET /tasks` 한 번을 읽어 그린다. 프로젝트를 고르면 그 프로젝트만 — 이슈가 수천이면 한 판에 다 그리지 않는 것이
   첫 번째 배치 안이고(기본은 첫 프로젝트), 하위 접기 · 완료 숨기기가 두 번째다. */

const ZOOMS = [0.25, 0.4, 0.5, 0.67, 0.8, 1, 1.25, 1.5];

export function GraphPage() {
  const [issues, setIssues] = useState<Issue[] | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [project, setProject] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [foldKids, setFoldKids] = useState(true);
  const [hideDone, setHideDone] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [zoom, setZoom] = useState(1);
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState<string | null>(null);
  // 마지막 찾기가 못 찾은 말 — 치는 중에는 경고를 안 띄운다(누른 뒤의 사실만).
  const [miss, setMiss] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      setIssues(await tasksApi.list());
      setErr(null);
    } catch (e) {
      setErr(errText(e));
    }
  }, []);
  useEffect(() => {
    void load();
    projectApi.list().then((ps) => {
      setProjects(ps);
      // 여럿이면 첫 프로젝트부터 — 전부를 한 판에 그리는 것은 사람이 고를 때만.
      setProject((cur) => cur || (ps.length > 1 ? ps[0].id : ""));
    }, () => undefined);
  }, [load]);

  const scoped = useMemo(
    () => (issues ?? []).filter((t) => (project === "" ? true : project === "-" ? (t.projectId ?? null) === null : t.projectId === project)),
    [issues, project],
  );
  const view = useMemo(() => graphView(scoped, { foldKids, expanded, hideDone }), [scoped, foldKids, expanded, hideDone]);

  const fit = () => {
    const el = wrap.current?.querySelector<HTMLElement>(".graph");
    const { w, h } = layoutGraph(view);
    if (!el || !w) return;
    const s = Math.min(1, (el.clientWidth - 48) / w, (el.clientHeight - 48) / Math.max(h, NODE_H));
    setZoom(Math.max(ZOOMS[0], Math.round(s * 100) / 100));
  };
  const step = (dir: 1 | -1) => {
    const i = ZOOMS.findIndex((z) => z >= zoom - 1e-6);
    const at = i === -1 ? ZOOMS.length - 1 : i;
    setZoom(ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, ZOOMS[at] === zoom ? at + dir : dir > 0 ? at : at - 1))]);
  };
  /** 선택 조회 — id 가 맞으면 그것, 아니면 제목에 낱말이 든 첫 노드. 접힌 하위면 조상을 펼쳐서 보이게 한다. */
  const find = () => {
    const n = q.trim().toLowerCase();
    if (!n) return;
    const hit = scoped.find((t) => t.id.toLowerCase() === n) ?? scoped.find((t) => t.title.toLowerCase().includes(n) || t.id.toLowerCase().includes(n));
    if (!hit) {
      setFocus(null);
      setMiss(q.trim());
      return;
    }
    setMiss(null);
    if (foldKids) {
      const byId = new Map(scoped.map((t) => [t.id, t]));
      const up = new Set(expanded);
      for (let p = hit.parentId; p; p = byId.get(p)?.parentId ?? null) up.add(p);
      setExpanded(up);
    }
    if (hideDone && (hit.status === "done" || hit.status === "cancelled")) setHideDone(false);
    setFocus(hit.id);
  };
  const open = openId ? (issues ?? []).find((t) => t.id === openId) ?? null : null;

  return (
    <AppShell route="/graph">
      <div ref={wrap} className="graph-page">
        <PageHead title={tx("nav.graph")} lead={<p>{tx("graph.lead")}</p>} />
        <div className="b-filterbar">
          <div className="b-filterbar-chips">
            <button type="button" className={`b-chip${foldKids ? " is-on" : ""}`} aria-pressed={foldKids} onClick={() => setFoldKids((v) => !v)}>
              {tx("graph.foldKids")}
            </button>
            <button type="button" className={`b-chip${hideDone ? " is-on" : ""}`} aria-pressed={hideDone} onClick={() => setHideDone((v) => !v)}>
              {tx("graph.hideDone")}
            </button>
            <Select size="md" value={project} aria-label={tx("hist.project")} onChange={(e) => setProject(e.target.value)}>
              <option value="">{tx("hist.projectAll")}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              <option value="-">{tx("hist.projectNone")}</option>
            </Select>
          </div>
          <form
            className="b-filterbar-chips"
            onSubmit={(e) => {
              e.preventDefault();
              find();
            }}
          >
            <Input size="md" className="graph-find" placeholder={tx("graph.findHint")} aria-label={tx("graph.find")} value={q} onChange={(e) => setQ(e.target.value)} />
            <Button size="md" type="submit">
              {tx("graph.find")}
            </Button>
          </form>
          <div className="b-filterbar-end graph-zoom">
            <Button size="md" variant="ghost" aria-label={tx("graph.zoomOut")} onClick={() => step(-1)} disabled={zoom <= ZOOMS[0]}>
              −
            </Button>
            <span className="tnum" aria-live="polite">{Math.round(zoom * 100)}%</span>
            <Button size="md" variant="ghost" aria-label={tx("graph.zoomIn")} onClick={() => step(1)} disabled={zoom >= ZOOMS[ZOOMS.length - 1]}>
              +
            </Button>
            <Button size="md" onClick={fit}>
              {tx("graph.fit")}
            </Button>
          </div>
        </div>
        {issues ? (
          <div className="graph-facts">
            <span className="b-badge">{tx("graph.facts.nodes", { n: view.nodes.length })}</span>
            <span className="b-badge">{tx("graph.facts.edges", { n: view.edges.length })}</span>
            <span className="b-badge">{tx("graph.facts.folded", { n: view.folded })}</span>
            <span className="b-badge">{tx("graph.facts.hidden", { n: view.hiddenDone })}</span>
            <span className="caption">{tx("graph.legend")}</span>
          </div>
        ) : null}
        {miss ? <Alert tone="warning">{tx("graph.notFound", { q: miss })}</Alert> : null}
        {err ? <Alert tone="error">{tx("graph.loadFailed", { err })}</Alert> : null}
        {!issues && !err ? <Loading /> : null}
        {issues && view.nodes.length === 0 ? (
          <EmptyState title={tx("graph.empty")} desc={tx(scoped.length ? "graph.emptyHidden" : "graph.emptyDesc")} />
        ) : null}
        {issues && view.nodes.length ? (
          <GraphCanvas
            view={view}
            zoom={zoom}
            focus={focus}
            foldKids={foldKids}
            expanded={expanded}
            onOpen={setOpenId}
            onToggle={(id) =>
              setExpanded((x) => {
                const n = new Set(x);
                if (n.has(id)) n.delete(id);
                else n.add(id);
                return n;
              })
            }
          />
        ) : null}
        {open ? (
          <IssueDetailModal
            t={open}
            all={issues ?? []}
            onOpen={setOpenId}
            onClose={() => setOpenId(null)}
            onChanged={() => void load()}
            onDeleted={() => {
              setOpenId(null);
              void load();
            }}
          />
        ) : null}
      </div>
    </AppShell>
  );
}

